import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Check, X, Undo2, Loader2, ChevronRight, ShieldCheck } from 'lucide-react';
import api from '../../services/api';
import { formatMonto, formatFecha, formatFechaHora } from '../../utils/format';
import { pref } from '../../utils/moneda';

// ============================================================
// IA en el cierre de mes (aprobado por Armando 2026-10-14, maqueta
// https://claude.ai/artifact/99mfDt6kTM5f9nQYKqCk3c). Dos bloques bajo las
// etapas del cierre (#111):
//   - Análisis del mes: avisos de colores contra el mes anterior y el
//     promedio de 3 meses, cada uno con su enlace.
//   - Revisión movimiento por movimiento: hallazgos con Aceptar / Descartar.
//     Mientras quede alguno por revisar, el punto "Revisión con IA" del
//     checklist sigue en rojo. Nada cambia sin clic.
// La lógica vive en el servidor (services/cierreIA.js).
// ============================================================

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const nombreMes = (mes) => `${MESES[Number(mes.slice(5, 7)) - 1]} ${mes.slice(0, 4)}`;
const soloMes = (mes) => MESES[Number(mes.slice(5, 7)) - 1];

const PUNTO = { rojo: 'bg-red-500', ambar: 'bg-amber-500', verde: 'bg-green-500' };

const ENLACES = {
  movimientos: { ir: '/movimientos', texto: 'Ver movimientos' },
  categorizar: { ir: '/movimientos', texto: 'Categorizar' },
  resultados: { ir: '/reportes/estado-resultados', texto: 'Estado de resultados' },
  cxp: { ir: '/reportes/antiguedad-por-pagar', texto: 'Cuentas por pagar' },
  cxc: { ir: '/reportes/antiguedad-por-cobrar', texto: 'Cuentas por cobrar' },
  conciliacion: { ir: '/conciliacion', texto: 'Conciliar' },
  inusuales: { ir: '/reportes/movimientos-inusuales', texto: 'Ver inusuales' }
};

const TIPOS = {
  categoria: { texto: 'Categoría sugerida', clase: 'bg-blue-50 text-blue-700' },
  duplicado: { texto: 'Posible duplicado', clase: 'bg-red-50 text-red-700' },
  monto_raro: { texto: 'Monto raro', clase: 'bg-amber-50 text-amber-700' },
  sin_factura: { texto: 'Sin factura', clase: 'bg-violet-50 text-violet-700' },
  contra_regla: { texto: 'Contra una regla', clase: 'bg-amber-50 text-amber-800' }
};
const CAMBIA_CATEGORIA = ['categoria', 'contra_regla'];

const btnIA = 'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed';

function esperar(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function CierreIA({ mes, estadoMes, enCurso, rolActual, onCambio }) {
  const [ia, setIa] = useState(null);
  const [corriendo, setCorriendo] = useState(null); // 'analisis' | 'revision'
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [ocupado, setOcupado] = useState(null); // id del hallazgo que se está guardando

  const puede = ['contador', 'gerente', 'admin'].includes(rolActual) && estadoMes !== 'cerrado';
  const puedeReglas = ['contador', 'gerente', 'admin'].includes(rolActual);
  const base = `/reportes/cierre/${mes}/ia`;

  const cargar = () => api.get(base).then(({ data }) => setIa(data)).catch(() => setIa(null));

  useEffect(() => {
    setIa(null);
    setError('');
    setAviso('');
    setFiltro('todos');
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes]);

  // Al pasar a "En revisión" la revisión arranca sola en el servidor: se
  // consulta cada 5 s (hasta 2 min) para mostrarla cuando termine.
  const esperandoAuto = estadoMes === 'revision' && ia && !ia.revision_en && !corriendo;
  useEffect(() => {
    if (!esperandoAuto) return undefined;
    let vueltas = 0;
    const t = setInterval(async () => {
      vueltas += 1;
      const { data } = await api.get(base).catch(() => ({ data: null }));
      if (data) setIa(data);
      if (data?.revision_en) { clearInterval(t); onCambio(); }
      if (vueltas >= 24) clearInterval(t);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esperandoAuto, mes]);

  async function correr(tipo) {
    setCorriendo(tipo);
    setError('');
    setAviso('');
    try {
      const { data } = await api.post(`${base}/${tipo === 'analisis' ? 'analizar' : 'revisar'}`);
      let t;
      do {
        await esperar(2500);
        ({ data: t } = await api.get(`/reportes/cierre/ia/trabajo/${data.job_id}`));
      } while (t.estado === 'procesando');
      if (t.estado === 'error') setError(t.error);
      else if (t.aviso) setAviso(t.aviso);
      await cargar();
      if (tipo === 'revision') await onCambio();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo completar el análisis');
    } finally {
      setCorriendo(null);
    }
  }

  async function cambiar(h, estado) {
    setOcupado(h.id);
    setError('');
    try {
      await api.put(`${base}/hallazgos/${h.id}`, { estado });
      await cargar();
      await onCambio();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
    } finally {
      setOcupado(null);
    }
  }

  const hallazgos = useMemo(() => ia?.hallazgos || [], [ia]);
  const visibles = useMemo(() => hallazgos.filter((h) => filtro === 'todos' || h.tipo === filtro), [hallazgos, filtro]);
  const pendientesVisibles = visibles.filter((h) => h.estado === 'pendiente');
  const cuenta = (estado) => hallazgos.filter((h) => h.estado === estado).length;

  async function descartarVisibles() {
    const n = pendientesVisibles.length;
    if (!window.confirm(`¿Descartar los ${n} hallazgos pendientes que se ven? No cambia ningún movimiento.`)) return;
    setOcupado('varios');
    setError('');
    try {
      await api.post(`${base}/descartar`, { ids: pendientesVisibles.map((h) => h.id) });
      await cargar();
      await onCambio();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
    } finally {
      setOcupado(null);
    }
  }

  if (!ia) return null;
  const analisis = ia.analisis;
  const comparado = analisis?.comparado_con;

  return (
    <>
      {/* A. Análisis del mes */}
      <section id="analisis-ia" className="bg-white rounded-xl shadow-sm overflow-hidden break-inside-avoid">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <Sparkles size={16} className="text-violet-600" /> Análisis del mes
            </h2>
            <p className="text-xs text-slate-500">
              {comparado
                ? `${nombreMes(mes)} contra ${soloMes(comparado.mes_anterior)} y contra el promedio de ${soloMes(comparado.desde)} a ${soloMes(comparado.mes_anterior)}`
                : `${nombreMes(mes)} contra el mes anterior y el promedio de los 3 meses anteriores`}
            </p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            {ia.analisis_en && (
              <span className="text-xs text-slate-400">
                Analizado {formatFechaHora(ia.analisis_en)}{ia.analisis_por ? ` por ${ia.analisis_por}` : ''}
                {ia.analisis_costo_usd != null && ` · ≈ $${formatMonto(ia.analisis_costo_usd, 4)}`}
              </span>
            )}
            {puede && (
              <button type="button" className={btnIA} disabled={!!corriendo || enCurso} onClick={() => correr('analisis')}
                title={enCurso ? 'El mes todavía no termina' : 'Compara el mes con el anterior y con el promedio de 3 meses'}>
                {corriendo === 'analisis' ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {corriendo === 'analisis' ? 'Analizando…' : analisis ? 'Volver a analizar' : 'Analizar con IA'}
              </button>
            )}
          </div>
        </div>
        {!analisis ? (
          <p className="px-4 py-3 text-sm text-slate-500">
            Todavía no se analizó este mes. La IA compara cada partida contra el mes anterior y el promedio de 3 meses y avisa qué mirar antes de cerrar.
          </p>
        ) : (
          <ul className="px-4 py-3 space-y-2">
            {analisis.avisos.map((a, i) => {
              const enlace = ENLACES[a.enlace];
              return (
                <li key={i} className="grid grid-cols-[10px_1fr_auto] gap-3 items-start rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
                  <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ${PUNTO[a.nivel] || 'bg-slate-400'}`} />
                  <div className="min-w-0">
                    <p className="text-slate-700"><b className="text-slate-800">{/[.!?:]$/.test(a.titulo) ? a.titulo : `${a.titulo}.`}</b> {a.detalle}</p>
                    {a.cifras && <p className="mt-0.5 text-xs text-slate-500 tabular-nums">{a.cifras}</p>}
                  </div>
                  {enlace && (
                    <Link to={enlace.ir} className="inline-flex items-center gap-0.5 text-primary-600 hover:text-primary-700 whitespace-nowrap print:hidden">
                      {enlace.texto} <ChevronRight size={14} />
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* B. Revisión movimiento por movimiento */}
      <section id="revision-ia" className="bg-white rounded-xl shadow-sm overflow-hidden break-inside-avoid">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <Sparkles size={16} className="text-violet-600" /> Revisión movimiento por movimiento
            </h2>
            <p className="text-xs text-slate-500">
              {ia.revision_en
                ? `Se revisaron los ${ia.revision_movimientos} movimientos del mes. Cada hallazgo se acepta o se descarta; nada cambia sin tu clic.`
                : 'Busca categorías mal puestas, posibles duplicados, montos raros y pagos sin factura. Arranca sola al empezar el cierre.'}
            </p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            {ia.revision_en && (
              <span className="text-xs text-slate-400">
                Corrió {formatFechaHora(ia.revision_en)}{ia.revision_por ? ` por ${ia.revision_por}` : ''}
                {ia.revision_costo_usd != null && ` · ≈ $${formatMonto(ia.revision_costo_usd, 4)}`}
              </span>
            )}
            {puede && (
              <button type="button" className={btnIA} disabled={!!corriendo || enCurso} onClick={() => correr('revision')}
                title={enCurso ? 'El mes todavía no termina' : 'Revisa todos los movimientos del mes; lo ya aceptado o descartado no se vuelve a preguntar'}>
                {corriendo === 'revision' ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {corriendo === 'revision' ? 'Revisando…' : ia.revision_en ? 'Volver a revisar' : 'Revisar con IA'}
              </button>
            )}
          </div>
        </div>

        {esperandoAuto && (
          <p className="px-4 pt-3 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Si la revisión arrancó al empezar el cierre, aparece aquí al terminar.
          </p>
        )}
        {error && <p className="px-4 pt-3 text-sm text-red-600">{error}</p>}
        {aviso && <p className="px-4 pt-3 text-sm text-amber-700">{aviso}</p>}

        {ia.revision_en && (
          <div className="px-4 py-3 space-y-3">
            <div className="flex flex-wrap gap-2 print:hidden">
              {[['todos', 'Todos'], ...Object.entries(TIPOS).map(([k, t]) => [k, t.texto])].map(([k, texto]) => {
                const n = k === 'todos' ? hallazgos.length : hallazgos.filter((h) => h.tipo === k).length;
                return (
                  <button key={k} type="button" onClick={() => setFiltro(k)}
                    className={`rounded-full px-3 py-1 text-xs border ${filtro === k ? 'bg-slate-800 text-white border-transparent' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                    {texto} ({n})
                  </button>
                );
              })}
            </div>

            {hallazgos.length === 0 ? (
              <p className="text-sm text-slate-600">La revisión no encontró nada raro en este mes.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                    <tr>
                      <th className="py-2 pr-3">Hallazgo</th>
                      <th className="py-2 pr-3">Movimiento</th>
                      <th className="py-2 pr-3 text-right">Monto</th>
                      <th className="py-2 pr-3">Sugerencia</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((h) => {
                      const t = TIPOS[h.tipo];
                      const contraparte = h.cliente_nombre || h.nombre_receptor;
                      return (
                        <tr key={h.id} className={`border-b last:border-0 align-top ${h.estado === 'aceptado' ? 'bg-green-50/60' : ''} ${h.estado === 'descartado' ? 'opacity-50' : ''}`}>
                          <td className="py-2.5 pr-3">
                            <span className={`text-[11px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 whitespace-nowrap ${t.clase}`}>{t.texto}</span>
                          </td>
                          <td className="py-2.5 pr-3">
                            <div className="text-slate-700">
                              {formatFecha(h.fecha)}{contraparte ? ` · ${contraparte}` : ''}{h.descripcion ? ` · "${h.descripcion}"` : ''}
                            </div>
                            <div className="text-xs text-slate-400">
                              {h.cuenta_nombre}{h.categoria_actual ? ` · ${h.categoria_actual}` : ' · sin categoría'}
                              {(h.descripcion || h.referencia) && (
                                <Link to={`/movimientos?q=${encodeURIComponent(h.referencia || h.descripcion)}`} className="ml-2 text-primary-600 hover:underline print:hidden">Ver</Link>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums whitespace-nowrap">{pref(h.moneda)}{formatMonto(h.monto)}</td>
                          <td className="py-2.5 pr-3">
                            <div className="text-slate-800">{h.sugerencia}</div>
                            <div className="text-xs text-slate-500">{h.motivo}</div>
                            {puedeReglas && h.tipo === 'categoria' && h.estado === 'aceptado' && h.categoria_sugerida_id && (contraparte || h.descripcion) && (
                              <Link className="text-xs text-primary-600 hover:underline print:hidden"
                                to={`/categorias?reglas=nueva&texto=${encodeURIComponent((contraparte || h.descripcion).slice(0, 100))}&categoria_id=${h.categoria_sugerida_id}`}
                                title="Para que los próximos movimientos parecidos lleguen ya con esta categoría">
                                Crear regla con esto
                              </Link>
                            )}
                          </td>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center justify-end gap-3 print:hidden">
                              {h.estado === 'pendiente' ? (
                                puede && (
                                  <>
                                    <button type="button" disabled={!!ocupado} onClick={() => cambiar(h, 'aceptado')}
                                      title={h.tipo === 'contra_regla' ? 'Aceptar: cambia a la categoría de la regla' : h.tipo === 'categoria' ? 'Aceptar: cambia la categoría a la sugerida' : 'Aceptar: lo corrijo (no cambia el movimiento)'}
                                      className="text-slate-400 hover:text-green-600 disabled:opacity-50">
                                      <Check size={18} />
                                    </button>
                                    <button type="button" disabled={!!ocupado} onClick={() => cambiar(h, 'descartado')}
                                      title="Descartar: está bien así" className="text-slate-400 hover:text-red-600 disabled:opacity-50">
                                      <X size={18} />
                                    </button>
                                  </>
                                )
                              ) : (
                                <>
                                  <span className={`text-xs font-semibold whitespace-nowrap ${h.estado === 'aceptado' ? 'text-green-700' : 'text-slate-500'}`}
                                    title={h.revisado_por_nombre ? `${h.revisado_por_nombre} · ${formatFechaHora(h.revisado_en)}` : undefined}>
                                    {h.estado === 'aceptado' ? 'Aceptado' : 'Descartado'}
                                  </span>
                                  {puede && (
                                    <button type="button" disabled={!!ocupado} onClick={() => cambiar(h, 'pendiente')}
                                      title={CAMBIA_CATEGORIA.includes(h.tipo) && h.estado === 'aceptado' ? 'Deshacer: devuelve la categoría anterior' : 'Deshacer'}
                                      className="text-slate-400 hover:text-slate-700 disabled:opacity-50">
                                      <Undo2 size={16} />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {hallazgos.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className={cuenta('pendiente') ? 'text-amber-700' : 'text-green-700'}>
                  {hallazgos.length} hallazgos · {cuenta('aceptado')} aceptados · {cuenta('descartado')} descartados · {cuenta('pendiente')} por revisar.
                  {cuenta('pendiente') > 0 && ' Mientras queden por revisar, el punto "Revisión con IA" del checklist queda en rojo.'}
                </p>
                {puede && pendientesVisibles.length > 1 && (
                  <button type="button" disabled={!!ocupado} onClick={descartarVisibles} className="text-xs text-slate-500 underline hover:text-slate-700 print:hidden">
                    Descartar los {pendientesVisibles.length} pendientes que se ven
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <details className="px-4 pb-3 text-xs text-slate-500 print:hidden">
          <summary className="cursor-pointer inline-flex items-center gap-1"><ShieldCheck size={13} /> Qué sale del servidor</summary>
          <ul className="mt-1 ml-5 list-disc space-y-0.5">
            <li>Análisis del mes: solo totales por partida y conteos. Sin nombres de clientes ni proveedores.</li>
            <li>Revisión movimiento por movimiento: fecha, descripción, monto, categoría y nombre del proveedor o cliente de cada movimiento.</li>
            <li>Va a la misma IA que hoy lee las facturas de compra, una empresa por vez. Lo aceptado y descartado queda en Auditoría.</li>
          </ul>
        </details>
      </section>
    </>
  );
}
